import React, {useState} from "react";
import {Box, IconButton, InputAdornment, InputBase, TextField, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {EyeIcon, EyeOffIcon} from "@hugeicons/core-free-icons";

// Flat, borderless, rounded field chrome shared by the registry create/edit dialogs - matches the
// InputBase + controlSx pattern used across registry-project (see CLAUDE.md design system).
export const controlSx = {
  height: 40, display: "flex", alignItems: "center", gap: 1, px: 1.25,
  borderRadius: "10px", bgcolor: "#FAFAFD", color: "#6B6B80", fontSize: 13,
};

export const FieldLabel = ({children, required}) => (
  <Typography sx={{fontSize: 12, fontWeight: 600, color: "#6B6B80", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.05em"}}>
    {children}{required && <Box component={"span"} sx={{color: "#DC2626"}}> *</Box>}
  </Typography>
);

export const FieldHelper = ({error, children}) => (
  <Typography sx={{fontSize: 12, color: error ? "#DC2626" : "#8A8AA0", mt: 0.5}}>{children}</Typography>
);

export const selectSx = {
  ...controlSx,
  width: "100%",
  "&:hover": {bgcolor: "#ECECF3"},
  "& .MuiOutlinedInput-notchedOutline": {border: "none"},
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {border: "none"},
  "& .MuiSelect-select": {display: "flex", alignItems: "center", py: "0 !important", pl: "12px !important", pr: "30px !important", minHeight: "0 !important"},
  "& .MuiSelect-icon": {color: "#6B6B80"},
};

// Flat chrome for an MUI Autocomplete's input (freeSolo provider pickers, member search, ...)
export const autocompleteInputSx = {
  "& .MuiOutlinedInput-root": {
    ...controlSx, height: "auto", minHeight: 40, py: "5px !important",
    "& fieldset": {border: "none"},
    "&:hover": {bgcolor: "#ECECF3"},
    "&.Mui-focused": {bgcolor: "#ECECF3"},
  },
};

export const FlatAutocompleteInput = (props) => <TextField {...props} sx={{...autocompleteInputSx, ...props.sx}}/>;

export const modalPaperSx = (width = 480) => ({
  position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
  width: {xs: "calc(100% - 24px)", md: `min(${width}px, 92vw)`}, maxHeight: "90vh",
  bgcolor: "#fff", borderRadius: "20px", boxShadow: "0 24px 70px rgba(35, 38, 77, 0.22)",
  display: "flex", flexDirection: "column", outline: "none",
});

export const modalCancelSx = {
  textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: "10px",
  color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

export const modalSaveSx = {
  textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: "10px",
  color: "#fff", bgcolor: "#374151", "&:hover": {bgcolor: "#1F2937"},
  "&.Mui-disabled": {bgcolor: "#D1D5DB", color: "#9CA3AF"},
};

// Plain flat button (Cancel / Save) - a <button> Box rather than MUI's Button so modalCancelSx/modalSaveSx
// apply without fighting MUI's own variant styles
export const ModalButton = ({sx, disabled, ...props}) => (
  <Box
    component={"button"}
    type={"button"}
    disabled={disabled}
    {...props}
    sx={{
      border: "none", px: 2.5, py: 1.1, cursor: disabled ? "default" : "pointer",
      ...(disabled && {opacity: 0.6, pointerEvents: "none"}),
      ...sx,
    }}
  />
);

// Write-only secret: can be set on creation but is never returned or editable afterwards
export const SecretField = ({value, onChange, placeholder, name}) => {
  const [show, setShow] = useState(false);
  return (
    <Box sx={controlSx}>
      <InputBase
        name={name}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputProps={{"aria-label": placeholder}}
        sx={{flex: 1, fontSize: 13, color: "#23264D"}}
        endAdornment={
          <InputAdornment position={"end"}>
            <IconButton size={"small"} onClick={() => setShow(prev => !prev)} edge={"end"}>
              <HugeiconsIcon icon={show ? EyeOffIcon : EyeIcon} size={16}/>
            </IconButton>
          </InputAdornment>
        }
      />
    </Box>
  );
};
